import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CarService } from 'src/app/services/car/car.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { UserService } from 'src/app/services/user/user.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { PortService } from 'src/app/services/port/port.service';
import { VehicleTypeService } from 'src/app/services/vehicle-type/vehicle-type.service';
import { BodyTypeService } from 'src/app/services/body-type/body-type.service';
import { ModelCodeService } from 'src/app/services/model-code/model-code.service';
import { AccessoriesService } from 'src/app/services/accessories/accessories.service';
import { FUEL_LIST } from 'src/app/shared/data/fuel-list';
import { TRANSMISSION_LIST } from 'src/app/shared/data/transmission-list';
import { COLOR_LIST } from 'src/app/shared/data/color-list';
import { MF_YEAR_LIST } from 'src/app/shared/data/years-list';
import Swal from 'sweetalert2';
import { FuelService } from 'src/app/services/fuel/fuel.service';

@Component({
  selector: 'app-vh-manager-edit',
  templateUrl: './vh-manager-edit.component.html',
  styleUrls: ['./vh-manager-edit.component.scss']
})
export class VhManagerEditComponent extends DestructorComponent implements OnInit {

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('multiImageInput') multiImageInput!: ElementRef<HTMLInputElement>;

  vehicleForm: FormGroup;
  isLoading: boolean = false;
  isSaving: boolean = false;
  vehicleId: string = '';
  vehicle: any = null;

  // Image upload properties
  selectedImages: File[] = [];
  uploadingImages: boolean = false;
  uploadProgress: { current: number; total: number } = { current: 0, total: 0 };
  uploadedImageUrls: string[] = [];

  // Dropdown data
  makesList: any[] = [];
  modelsList: any[] = [];
  yardsList: any[] = [];
  salesPersonList: any[] = [];
  countriesList: any[] = [];
  portsList: any[] = [];
  vehicleTypesList: any[] = [];
  bodyTypesList: any[] = [];
  modelCodesList: any[] = [];
  fuelTypesList = FUEL_LIST;
  transmissionList = TRANSMISSION_LIST;
  colorsList = COLOR_LIST;
  yearsList = MF_YEAR_LIST;

  // Dynamic accessories data grouped by type
  goodPointsAccessories: any[] = [];
  mechanicalFaultsAccessories: any[] = [];
  accessoriesAndOptionsAccessories: any[] = [];
  allAccessories: any[] = [];

  // Grouped accessories for Accessories & Options
  groupedAccessoriesAndOptions: { [key: string]: any[] } = {};
  accessoryGroupNames: string[] = [];





  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private _carService: CarService,
    private _vehicleService: VehicleService,
    private _generalService: GeneralService,
    private _userService: UserService,
    private _globalStateService: GlobalStateService,
    private _portService: PortService,
    private _vehicleTypeService: VehicleTypeService,
    private _bodyTypeService: BodyTypeService,
    private _modelCodeService: ModelCodeService,
    private _fuelService: FuelService,
    private _accessoriesService: AccessoriesService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.vehicleId = params['vehicleId'];
      if (this.vehicleId) {
        // Load accessories first, then load vehicle data
        // This ensures form controls exist before populating values
        this.loadAccessoriesAndVehicle();
      }
    });
    // Load other dropdown data in parallel
    this.loadMakes();
    this.loadSalesPersons();
    this.loadCountries();
    this.loadVehicleTypes();
    this.loadBodyTypes();
    this.loadFuelTypes();
  }

  private initializeForm(): void {
    this.vehicleForm = this.fb.group({
      // Basic Information - New Fields
      show_on_home_page: [false],
      vehicle_type_radio: ['new'],
      status_radio: ['none'],
      inventory_no: ['', [Validators.required]],
      status_dropdown: ['now on sale', { disabled: true }],
      stock_location: [''],
      stock_port: [''],
      fob_price: [''],
      special_offer: [''],
      auction_number: [''],
      lot_no: [''],
      auction_date: [''],
      pos_no: [''],
      purchased_from: [''],
      auction_price: [''],
      sales_person_id: [''],

      // Vehicle Details - New Fields
      vehicle_title: [''],
      vin_no: [''],
      vehicle_type_dropdown: [''],
      grade: [''],
      registration_month: [''],
      registration_year: [''],
      body_type: [''],
      mileage: ['', [Validators.required, Validators.min(0)]],
      manufacture_month: [''],
      manufacture_year: [''],
      company_slug: [''],
      engine_capacity: [''],
      interior_grade: [''],
      slug: [''],
      seating_capacity: [''],
      exterior_grade: [''],
      model_code: [''],
      m3: [''],
      drive_2wd_4wd: [''],
      car_width: [0],
      car_height: [0],
      car_length: [0],
      transmission: ['', [Validators.required]],
      loading_capacity: [''],
      video_url: [''],
      fuel_type_slug: ['', [Validators.required]],
      weight: [''],
      hours_machinery: [''],
      exterior_color: [''],
      doors: [''],
      drive_type: [''],
      hs_code: [''],
      engine_number: [''],

      // Existing fields to preserve
      name: ['', [Validators.required]],
      company_name: ['', [Validators.required]],
      category_id: [1],
      year: ['', [Validators.required]],
      fuel_type: [''],
      transmission_slug: [''],
      color: [''],
      color_slug: [''],
      location: [''],
      price: [''],
      one_line_description: [''],
      engine: [''],
      inspection_expiry_date: [''],

      // Additional Vehicle Details (non-duplicates only)
      car_page: [''],
      car_id: [''],
      license_type: [''],
      seats: [''],
      auction_grade: [''],
      interior_color: [''],
      steering: [''],
      registration_number: [''],
      auction_house: [''],
      other_specifications: [''],
      remarks: [''],

      // Vehicle Management
      car_no: [''],
      yard_id: [''],
      yard_date: [''],
      type: ['aucnet'],

      // Accessories & Options - Column 1
      acc_air_conditioner: [false],
      acc_abs: [false],
      acc_airbag: [false],
      acc_alloy_wheels: [false],
      acc_anti_lock_package: [false],
      acc_auto_cruise: [false],
      acc_auto_light: [false],
      acc_keyless_start_navigation: [false],
      acc_navigation_system: [false],
      acc_power_seat: [false],
      acc_power_steering: [false],
      acc_remote_key: [false],
      acc_remote_keyless_entry: [false],

      // Accessories & Options - Column 2
      acc_4_door_hardtop: [false],
      acc_4_door_sedan: [false],
      acc_body_kit: [false],
      acc_body_stripes: [false],
      acc_bucket_seats: [false],
      acc_burn_light: [false],
      acc_cd_player: [false],
      acc_central_locking: [false],
      acc_chrome_garnish: [false],
      acc_dual_airbag: [false],
      acc_electric_mirrors: [false],
      acc_fog_lights: [false],
      acc_hid_lights: [false],

      // Accessories & Options - Column 3
      acc_immobilizer: [false],
      acc_low_floor: [false],
      acc_low_km: [false],
      acc_new_paint: [false],
      acc_new_tires: [false],
      acc_one_owner: [false],
      acc_original_paint: [false],
      acc_power_windows: [false],
      acc_rear_spoiler: [false],
      acc_side_airbag: [false],
      acc_sunroof: [false],
      acc_turbo: [false],
      acc_tv: [false],

      // Accessories & Options - Column 4
      acc_other_working_points: [false],
      acc_abs_brakes: [false],
      acc_air_bags: [false],
      acc_alarm: [false],
      acc_automatic_climate: [false],
      acc_cassette_stereo: [false],
      acc_central_door_locking: [false],
      acc_cruise_control: [false],
      acc_driver_airbag: [false],
      acc_dual_front_airbag: [false],
      acc_electric_seats: [false],
      acc_multi_information: [false],
      acc_non_smoking_package: [false],

      // Accessories & Options - Column 5
      acc_power_mirrors: [false],
      acc_rear_camera: [false],
      acc_rear_defogger: [false],
      acc_rear_wiper: [false],
      acc_remote_central_locking: [false],
      acc_side_impact_door_beams: [false],
      acc_tachometer: [false],
      acc_trip_meter: [false],
      acc_tubeless_radial_tires: [false],
      acc_vanity_mirror: [false],
      acc_wheel_covers: [false],

      // Accessories & Options - Column 6
      acc_sound_system: [false],
      acc_bucket_seat: [false],
      acc_leather_seat: [false],
      acc_captain_seat: [false],
      acc_third_seat: [false],
      acc_walk_through: [false],
      acc_sun_roof: [false],
      acc_moon_roof: [false],
      acc_sliding_door: [false],
      acc_rear_window_wiper: [false],
      acc_tinted_glass: [false],

      // Good Points
      gp_transmission_smooth_available: [false],
      gp_new_tires: [false],
      gp_original_paint: [false],
      gp_upgraded_sound_system: [false],
      gp_turbo: [false],
      gp_leather_seats_good: [false],
      gp_tv_dvd: [false],
      gp_alloy_wheels: [false],
      gp_perfect_tires: [false],

      // Mechanical Faults
      mf_body_parts: [false],
      mf_air_conditioning: [false],
      mf_suspension: [false],
      mf_other_minor: [false],
      mf_electrical: [false],
      mf_interior_lights: [false],
      mf_power_windows: [false],
      mf_engine_noise: [false],
      mf_engine_overhaul: [false],
      mf_transmission_slipping: [false],
      mf_ac_not_cold: [false],
      mf_rust_accident_history: [false],
      mf_body_damage_history: [false],
      mf_mechanical_issues: [false],

      // Other Options
      oo_electrical_system_complete: [false],
      oo_abs_and_srs: [false],
      oo_mileage_unknown: [false],
      oo_rear_accident_history: [false],
      oo_other_issues: [false],

      // Features Remarks
      features_remarks: [''],

      // Additional Information
      additional_information: ['']
    });
  }

  private loadAccessoriesAndVehicle(): void {
    // Load accessories first to ensure form controls exist
    this.subscriptions.push(
      this._accessoriesService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const accessories = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.allAccessories = accessories;

            // Group accessories by type
            this.goodPointsAccessories = accessories.filter((acc: any) => acc.type === 'Good Points');
            this.mechanicalFaultsAccessories = accessories.filter((acc: any) => acc.type === 'Mechanical Faults');
            this.accessoriesAndOptionsAccessories = accessories.filter((acc: any) => acc.type === 'Accessories & Options');

            // Group Accessories & Options by group name
            this.groupAccessoriesAndOptions();

            // Add dynamic form controls for accessories
            this.addDynamicFormControls();

            // Now that form controls exist, load the vehicle data
            this.loadVehicle();
          }
        },
        error: (error: any) => {
          console.error('Error loading accessories:', error);
          // Even if accessories fail, try to load vehicle
          this.loadVehicle();
        }
      })
    );
  }

  private loadFuelTypes(): void {
    this.subscriptions.push(
      this._fuelService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const fuelTypes = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.fuelTypesList = fuelTypes.map((fuel: any) => ({
              value: fuel.slug || fuel._id,
              label: fuel.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading fuel types:', error);
        }
      })
    );
  }



  private groupAccessoriesAndOptions(): void {
    this.groupedAccessoriesAndOptions = {};
    this.accessoryGroupNames = [];

    // Group accessories by their group property
    this.accessoriesAndOptionsAccessories.forEach((accessory: any) => {
      const groupName = accessory.group || 'Other'; // Default to 'Other' if no group

      if (!this.groupedAccessoriesAndOptions[groupName]) {
        this.groupedAccessoriesAndOptions[groupName] = [];
        this.accessoryGroupNames.push(groupName);
      }

      this.groupedAccessoriesAndOptions[groupName].push(accessory);
    });

    // Sort group names alphabetically for consistent display
    this.accessoryGroupNames.sort();
  }

  private addDynamicFormControls(): void {
    // Add form controls for all dynamic accessories
    this.allAccessories.forEach((accessory: any) => {
      if (accessory.slug && !this.vehicleForm.get(accessory.slug)) {
        this.vehicleForm.addControl(accessory.slug, this.fb.control(false));
      }
    });
  }

  private loadSalesPersons(): void {
    this.subscriptions.push(
      this._userService.GetData('1', '', { paginate: 'false', role_type: 30 }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const salesPersons = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.salesPersonList = salesPersons.map((person: any) => ({
              value: person._id || person.doc_id || '',
              label: `${person.title || ''} ${person.name || ''}`.trim()
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading sales persons:', error);
          this.salesPersonList = [];
        }
      })
    );
  }

  private loadMakes(): void {
    this.subscriptions.push(
      this._generalService.GetMakes().subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.makesList = response.data.map((make: any) => ({
              value: make.slug || make._id,
              label: make.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading makes:', error);
        }
      })
    );
  }

  private loadModels(makeSlug: string): void {
    if (!makeSlug) {
      this.modelsList = [];
      return;
    }

    this.subscriptions.push(
      this._generalService.GetModels(makeSlug).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.modelsList = response.data.map((model: any) => ({
              value: model.slug || model._id,
              label: model.name
            }));
            this.vehicleForm.patchValue({
              slug: this.vehicle?.slug || ''
            });
          }
        },
        error: (error: any) => {
          console.error('Error loading models:', error);
          this.modelsList = [];
        }
      })
    );
  }

  // private loadYards(): void {
  //   this.subscriptions.push(
  //     this._generalService.GetYards().subscribe({
  //       next: (response: any) => {
  //         if (response.status && response.data) {
  //           this.yardsList = response.data.map((yard: any) => ({
  //             value: yard._id || yard.id,
  //             label: yard.name
  //           }));
  //         }
  //       },
  //       error: (error: any) => {
  //         console.error('Error loading yards:', error);
  //       }
  //     })
  //   );
  // }

  private loadCountries(): void {
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe({
        next: (countries: any) => {
          if (countries && Array.isArray(countries)) {
            this.countriesList = countries.map((country: any) => ({
              value: country.code || country._id,
              label: country.name,
              code: country.code
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading countries:', error);
        }
      })
    );
  }

  private loadPorts(countryCode: string): void {
    if (!countryCode) {
      this.portsList = [];
      return;
    }

    this.subscriptions.push(
      this._portService.GetData('1', '', { country_code: countryCode, paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const ports = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.portsList = ports.map((port: any) => ({
              value: port._id || port.id,
              label: port.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading ports:', error);
          this.portsList = [];
        }
      })
    );
  }

  private loadVehicleTypes(): void {
    this.subscriptions.push(
      this._vehicleTypeService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const vehicleTypes = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.vehicleTypesList = vehicleTypes.map((type: any) => ({
              value: type._id || type.id,
              label: type.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading vehicle types:', error);
        }
      })
    );
  }

  private loadBodyTypes(): void {
    this.subscriptions.push(
      this._bodyTypeService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const bodyTypes = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.bodyTypesList = bodyTypes.map((type: any) => ({
              value: type._id || type.id,
              label: type.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading body types:', error);
        }
      })
    );
  }

  private loadModelCodes(modelSlug?: string): void {
    if (!modelSlug) {
      this.modelCodesList = [];
      return;
    }

    this.subscriptions.push(
      this._modelCodeService.GetData('1', '', { paginate: false, model_slug: modelSlug }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const modelCodes = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.modelCodesList = modelCodes.map((code: any) => ({
              value: code.code || code._id,
              label: code.name || code.code
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading model codes:', error);
        }
      })
    );
  }

  private loadVehicle(): void {
    this.isLoading = true;

    this.subscriptions.push(
      this._vehicleService.GetDataById(this.vehicleId).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.vehicle = response.data;
            this.populateForm(this.vehicle);

            // Load models if company_slug exists
            if (this.vehicle.company_slug) {
              this.loadModels(this.vehicle.company_slug);
            }

            // Load model codes if model slug exists
            if (this.vehicle.slug) {
              this.loadModelCodes(this.vehicle.slug);
            }

            if (this.vehicle.stock_location) {
              this.loadPorts(this.vehicle.stock_location);
            }
          } else {
            this.showError('Vehicle not found');
            this.router.navigate(['/vehicles/vehicle-manager']);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('Error loading vehicle:', error);
          this.showError('Error loading vehicle data');
          this.isLoading = false;
          this.router.navigate(['/vehicles/vehicle-manager']);
        }
      })
    );
  }

  private populateForm(vehicle: any): void {
    if (!vehicle) return;

    const formData: any = { ...vehicle };
    formData.auction_date = vehicle.auction_date_only;

    // Handle dynamic accessories data from API
    if (vehicle.good_points) {
      Object.keys(vehicle.good_points).forEach(key => {
        // Check if this is a dynamic accessory field
        const dynamicAccessory = this.allAccessories.find(acc => acc.slug === key);
        if (dynamicAccessory) {
          formData[key] = vehicle.good_points[key] === 1 || vehicle.good_points[key] === true;
        } else {
          // Handle legacy hardcoded fields with gp_ prefix
          const formFieldName = `gp_${key}`;
          if (this.vehicleForm.get(formFieldName)) {
            formData[formFieldName] = vehicle.good_points[key] === 1 || vehicle.good_points[key] === true;
          }
        }
      });
      delete formData.good_points;
    }

    if (vehicle.mechanical_faults) {
      Object.keys(vehicle.mechanical_faults).forEach(key => {
        // Check if this is a dynamic accessory field
        const dynamicAccessory = this.allAccessories.find(acc => acc.slug === key);
        if (dynamicAccessory) {
          formData[key] = vehicle.mechanical_faults[key] === 1 || vehicle.mechanical_faults[key] === true;
        } else {
          // Handle legacy hardcoded fields with mf_ prefix
          const formFieldName = `mf_${key}`;
          if (this.vehicleForm.get(formFieldName)) {
            formData[formFieldName] = vehicle.mechanical_faults[key] === 1 || vehicle.mechanical_faults[key] === true;
          }
        }
      });
      delete formData.mechanical_faults;
    }

    if (vehicle.accessories_and_options) {
      Object.keys(vehicle.accessories_and_options).forEach(key => {
        // Check if this is a dynamic accessory field
        const dynamicAccessory = this.allAccessories.find(acc => acc.slug === key);
        if (dynamicAccessory) {
          formData[key] = vehicle.accessories_and_options[key] === 1 || vehicle.accessories_and_options[key] === true;
        } else {
          // Handle legacy hardcoded fields with oo_ or acc_ prefix
          const ooFieldName = `oo_${key}`;
          const accFieldName = `acc_${key}`;

          if (this.vehicleForm.get(ooFieldName)) {
            formData[ooFieldName] = vehicle.accessories_and_options[key] === 1 || vehicle.accessories_and_options[key] === true;
          } else if (this.vehicleForm.get(accFieldName)) {
            formData[accFieldName] = vehicle.accessories_and_options[key] === 1 || vehicle.accessories_and_options[key] === true;
          }
        }
      });
      delete formData.accessories_and_options;
    }

    // Format date fields
    if (formData.yard_date) {
      formData.yard_date = this.formatDateForInput(formData.yard_date);
    }
    if (formData.inspection_expiry_date) {
      formData.inspection_expiry_date = this.formatDateForInput(formData.inspection_expiry_date);
    }

    if (formData.is_sold) {
      formData.status_dropdown = 'sold';
    } else {
      formData.status_dropdown = 'now on sale';
    }


    this.vehicleForm.patchValue(formData);

    // Ensure status_dropdown remains disabled after patching
    this.vehicleForm.get('status_dropdown')?.disable();
  }

  private formatDateForInput(dateValue: any): string {
    if (!dateValue) return '';

    // Handle different date formats
    let date: Date;

    if (typeof dateValue === 'number') {
      // Handle format like 20240800 (YYYYMMDD)
      const dateStr = dateValue.toString();
      if (dateStr.length === 8) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        date = new Date(`${year}-${month}-${day}`);
      } else {
        date = new Date(dateValue);
      }
    } else {
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) return '';

    return date.toISOString().split('T')[0];
  }

  onMakeChange(makeSlug: string): void {
    this.vehicleForm.patchValue({
      company_slug: makeSlug,
      slug: '', // Reset model when make changes
      model_code: '' // Reset model code when make changes
    });
    this.modelsList = [];
    this.modelCodesList = [];
    if (makeSlug) {
      this.loadModels(makeSlug);
    }
  }

  onModelChange(modelSlug: string): void {
    this.vehicleForm.patchValue({
      slug: modelSlug,
      model_code: '' // Reset model code when model changes
    });
    this.modelCodesList = [];
    if (modelSlug) {
      this.loadModelCodes(modelSlug);
    }
  }

  onFuelTypeChange(fuelType: string): void {
    const selectedFuel = this.fuelTypesList.find(fuel => fuel.value === fuelType);
    if (selectedFuel) {
      this.vehicleForm.patchValue({
        fuel_type: selectedFuel.label,
        fuel_type_slug: selectedFuel.value
      });
    }
  }

  onTransmissionChange(transmission: string): void {
    const selectedTransmission = this.transmissionList.find(trans => trans.value === transmission);
    if (selectedTransmission) {
      this.vehicleForm.patchValue({
        transmission: selectedTransmission.label,
        transmission_slug: selectedTransmission.value
      });
    }
  }

  onColorChange(color: string): void {
    const selectedColor = this.colorsList.find(col => col.value === color);
    if (selectedColor) {
      this.vehicleForm.patchValue({
        color: selectedColor.label,
        color_slug: selectedColor.value
      });
    }
  }

  openFile(): void {
    // Method for opening vehicle images - can be enhanced as needed
  }

  onStockLocationChange(countryCode: string): void {
    this.vehicleForm.patchValue({
      stock_port: ''
    });
    this.loadPorts(countryCode);
  }

  onSubmit(): void {
    if (this.vehicleForm.valid) {
      this.isSaving = true;

      const formData = this.prepareFormData();

      this.subscriptions.push(
        this._vehicleService.UpdateData(this.vehicleId, formData).subscribe({
          next: (response: any) => {
            if (response.status) {
              this.showSuccess('Vehicle updated successfully!');
              this.router.navigate(['/vehicles/vehicle-manager']);
            } else {
              this.showError(response.message || 'Failed to update vehicle');
            }
            this.isSaving = false;
          },
          error: (error: any) => {
            console.error('Error updating vehicle:', error);
            this.showError('Error updating vehicle. Please try again.');
            this.isSaving = false;
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.vehicleForm);
      this.showError('Please fill in all required fields correctly.');
    }
  }

  private prepareFormData(): any {
    const formValue = { ...this.vehicleForm.value };

    // Initialize grouped objects
    const good_points: any = {};
    const mechanical_faults: any = {};
    const accessories_and_options: any = {};

    // Process ONLY dynamic accessories from API
    this.allAccessories.forEach(accessory => {
      if (formValue[accessory.slug] !== undefined) {
        const value = formValue[accessory.slug] ? 1 : 0;

        if (accessory.type === 'Good Points') {
          good_points[accessory.slug] = value;
        } else if (accessory.type === 'Mechanical Faults') {
          mechanical_faults[accessory.slug] = value;
        } else if (accessory.type === 'Accessories & Options') {
          accessories_and_options[accessory.slug] = value;
        }

        delete formValue[accessory.slug]; // Remove from main object
      }
    });

    // Remove legacy hardcoded fields that should not be sent to API
    const legacyFieldsToRemove = [
      // Legacy Good Points fields (gp_ prefix)
      'gp_transmission_smooth_available', 'gp_new_tires', 'gp_original_paint', 'gp_upgraded_sound_system',
      'gp_turbo', 'gp_leather_seats_good', 'gp_tv_dvd', 'gp_alloy_wheels', 'gp_perfect_tires',

      // Legacy Mechanical Faults fields (mf_ prefix)
      'mf_body_parts', 'mf_air_conditioning', 'mf_suspension', 'mf_other_minor', 'mf_electrical',
      'mf_interior_lights', 'mf_power_windows', 'mf_engine_noise', 'mf_engine_overhaul', 'mf_transmission_slipping',
      'mf_ac_not_cold', 'mf_rust_accident_history', 'mf_body_damage_history', 'mf_mechanical_issues',

      // Legacy Other Options fields (oo_ prefix)
      'oo_electrical_system_complete', 'oo_abs_and_srs', 'oo_mileage_unknown', 'oo_rear_accident_history', 'oo_other_issues',

      // Legacy Accessories fields (acc_ prefix)
      'acc_air_conditioner', 'acc_abs', 'acc_airbag', 'acc_alloy_wheels', 'acc_anti_lock_package',
      'acc_auto_cruise', 'acc_auto_light', 'acc_keyless_start_navigation', 'acc_navigation_system',
      'acc_power_seat', 'acc_power_steering', 'acc_remote_key', 'acc_remote_keyless_entry',
      'acc_4_door_hardtop', 'acc_4_door_sedan', 'acc_body_kit', 'acc_body_stripes', 'acc_bucket_seats',
      'acc_burn_light', 'acc_cd_player', 'acc_central_locking', 'acc_chrome_garnish', 'acc_dual_airbag',
      'acc_electric_mirrors', 'acc_fog_lights', 'acc_hid_lights', 'acc_immobilizer', 'acc_low_floor',
      'acc_low_km', 'acc_new_paint', 'acc_new_tires', 'acc_one_owner', 'acc_original_paint',
      'acc_power_windows', 'acc_rear_spoiler', 'acc_side_airbag', 'acc_sunroof', 'acc_turbo', 'acc_tv',
      'acc_other_working_points', 'acc_abs_brakes', 'acc_air_bags', 'acc_alarm', 'acc_automatic_climate',
      'acc_cassette_stereo', 'acc_central_door_locking', 'acc_cruise_control', 'acc_driver_airbag',
      'acc_dual_front_airbag', 'acc_electric_seats', 'acc_multi_information', 'acc_non_smoking_package',
      'acc_power_mirrors', 'acc_rear_camera', 'acc_rear_defogger', 'acc_rear_wiper', 'acc_remote_central_locking',
      'acc_side_impact_door_beams', 'acc_tachometer', 'acc_trip_meter', 'acc_tubeless_radial_tires',
      'acc_vanity_mirror', 'acc_wheel_covers', 'acc_sound_system', 'acc_bucket_seat', 'acc_leather_seat',
      'acc_captain_seat', 'acc_third_seat', 'acc_walk_through', 'acc_sun_roof', 'acc_moon_roof',
      'acc_sliding_door', 'acc_rear_window_wiper', 'acc_tinted_glass',

      // Special fields that should not be sent separately
      'airbag', 'air_condition', 'sunroof', 'leather_seat', 'tv', 'keyless', 'smart_key', 'floormat', 'backcamera'
    ];

    // Remove all legacy fields from formValue
    legacyFieldsToRemove.forEach(field => {
      delete formValue[field];
    });

    // Handle date formatting - keep all dates as strings
    if (formValue.yard_date) {
      formValue.yard_date = String(formValue.yard_date);
    }
    if (formValue.inspection_expiry_date) {
      formValue.inspection_expiry_date = String(formValue.inspection_expiry_date);
    }
    else{
      formValue.inspection_expiry_date = '';
    }

    // Add grouped objects to the form data
    formValue.good_points = good_points;
    formValue.mechanical_faults = mechanical_faults;
    formValue.accessories_and_options = accessories_and_options;

    // Add pictures array to the form data
    if (this.vehicle && this.vehicle.pictures) {
      formValue.pictures = this.vehicle.pictures;
    }

    return formValue;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private showSuccess(message: string): void {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonText: 'OK'
    });
  }

  private showError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  // Form control getters for template
  get inventory_no() { return this.vehicleForm.get('inventory_no'); }
  get name() { return this.vehicleForm.get('name'); }
  get company_name() { return this.vehicleForm.get('company_name'); }
  get company_slug() { return this.vehicleForm.get('company_slug'); }
  get mileage() { return this.vehicleForm.get('mileage'); }
  get fuel_type_slug() { return this.vehicleForm.get('fuel_type_slug'); }
  get transmission() { return this.vehicleForm.get('transmission'); }
  get price() { return this.vehicleForm.get('price'); }

  // Additional getters for new fields
  get car_page() { return this.vehicleForm.get('car_page'); }
  get auction_number() { return this.vehicleForm.get('auction_number'); }
  get car_id() { return this.vehicleForm.get('car_id'); }
  get fob_price() { return this.vehicleForm.get('fob_price'); }
  get license_type() { return this.vehicleForm.get('license_type'); }
  get body_type() { return this.vehicleForm.get('body_type'); }
  get doors() { return this.vehicleForm.get('doors'); }
  get seats() { return this.vehicleForm.get('seats'); }
  get auction_grade() { return this.vehicleForm.get('auction_grade'); }
  get exterior_color() { return this.vehicleForm.get('exterior_color'); }
  get interior_color() { return this.vehicleForm.get('interior_color'); }
  get steering() { return this.vehicleForm.get('steering'); }
  get drive_type() { return this.vehicleForm.get('drive_type'); }
  get engine_capacity() { return this.vehicleForm.get('engine_capacity'); }
  get registration_number() { return this.vehicleForm.get('registration_number'); }
  get vin_no() { return this.vehicleForm.get('vin_no'); }
  get manufacture_month() { return this.vehicleForm.get('manufacture_month'); }
  get manufacture_year() { return this.vehicleForm.get('manufacture_year'); }
  get registration_month() { return this.vehicleForm.get('registration_month'); }
  get registration_year() { return this.vehicleForm.get('registration_year'); }
  get auction_house() { return this.vehicleForm.get('auction_house'); }
  get other_specifications() { return this.vehicleForm.get('other_specifications'); }
  get remarks() { return this.vehicleForm.get('remarks'); }

  // New field getters
  get show_on_home_page() { return this.vehicleForm.get('show_on_home_page'); }
  get vehicle_type_radio() { return this.vehicleForm.get('vehicle_type_radio'); }
  get status_radio() { return this.vehicleForm.get('status_radio'); }
  get status_dropdown() { return this.vehicleForm.get('status_dropdown'); }
  get stock_location() { return this.vehicleForm.get('stock_location'); }
  get stock_port() { return this.vehicleForm.get('stock_port'); }
  get special_offer() { return this.vehicleForm.get('special_offer'); }
  get lot_no() { return this.vehicleForm.get('lot_no'); }
  get auction_date() { return this.vehicleForm.get('auction_date'); }
  get pos_no() { return this.vehicleForm.get('pos_no'); }
  get purchased_from() { return this.vehicleForm.get('purchased_from'); }
  get auction_price() { return this.vehicleForm.get('auction_price'); }
  get sales_person_id() { return this.vehicleForm.get('sales_person_id'); }
  get vehicle_title() { return this.vehicleForm.get('vehicle_title'); }
  get vehicle_type_dropdown() { return this.vehicleForm.get('vehicle_type_dropdown'); }
  get grade() { return this.vehicleForm.get('grade'); }
  get interior_grade() { return this.vehicleForm.get('interior_grade'); }
  get seating_capacity() { return this.vehicleForm.get('seating_capacity'); }
  get exterior_grade() { return this.vehicleForm.get('exterior_grade'); }
  get model_code() { return this.vehicleForm.get('model_code'); }
  get m3() { return this.vehicleForm.get('m3'); }
  get drive_2wd_4wd() { return this.vehicleForm.get('drive_2wd_4wd'); }
  get loading_capacity() { return this.vehicleForm.get('loading_capacity'); }
  get video_url() { return this.vehicleForm.get('video_url'); }
  get weight() { return this.vehicleForm.get('weight'); }
  get hours_machinery() { return this.vehicleForm.get('hours_machinery'); }
  get hs_code() { return this.vehicleForm.get('hs_code'); }
  get engine_number() { return this.vehicleForm.get('engine_number'); }
  get car_width() { return this.vehicleForm.get('car_width'); }
  get car_height() { return this.vehicleForm.get('car_height'); }
  get car_length() { return this.vehicleForm.get('car_length'); }
  get slug() { return this.vehicleForm.get('slug'); }

  goBack(): void {
    this.router.navigate(['/vehicles/vehicle-manager']);
  }

  // Multi-image upload methods
  onMultiImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedImages = Array.from(input.files);
      this.uploadMultipleImages();
    }
  }

  triggerMultiImageUpload(): void {
    this.multiImageInput.nativeElement.click();
  }

  async uploadMultipleImages(): Promise<void> {
    if (this.selectedImages.length === 0) return;

    this.uploadingImages = true;
    this.uploadProgress = { current: 0, total: this.selectedImages.length };
    this.uploadedImageUrls = [];

    try {
      for (let i = 0; i < this.selectedImages.length; i++) {
        const file = this.selectedImages[i];
        this.uploadProgress.current = i + 1;

        const imageUrl = await this.uploadSingleImage(file);
        this.uploadedImageUrls.push(imageUrl);

        // Add to vehicle pictures array
        if (!this.vehicle.pictures) {
          this.vehicle.pictures = [];
        }
        this.vehicle.pictures.push(imageUrl);
      }

      this.showSuccess(`Successfully uploaded ${this.selectedImages.length} image(s)!`);
      this.selectedImages = [];

      // Reset file input
      if (this.multiImageInput) {
        this.multiImageInput.nativeElement.value = '';
      }

    } catch (error) {
      console.error('Error uploading images:', error);
      this.showError('Error uploading some images. Please try again.');
    } finally {
      this.uploadingImages = false;
      this.uploadProgress = { current: 0, total: 0 };
    }
  }

  private uploadSingleImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
        next: (res) => {
          const imageUrl = res.data.url;
          const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

          this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
            next: () => {
              resolve(imageUrl);
            },
            error: (error) => {
              reject(error);
            }
          });
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  removeImage(index: number): void {
    if (this.vehicle.pictures && this.vehicle.pictures.length > index) {
      this.vehicle.pictures.splice(index, 1);
    }
  }

  openImageModal(imageUrl: string): void {
    window.open(imageUrl, '_blank');
  }
}
